import { Segmented, Space, Typography, theme } from "antd";
import { useState } from "react";
import { COPYABLE } from "@/components/base";

/** Shell syntax for the env-var block: export lines, PowerShell, or plain KEY=value for env files. */
type EnvSyntax = "unix" | "windows" | "plain";

const ENV_SYNTAX_OPTIONS = [
	{ value: "unix", label: "macOS / Linux" },
	{ value: "windows", label: "Windows" },
	{ value: "plain", label: "Env file" },
];

/** Formats an agent's credential as env-var assignments in the chosen shell syntax. */
function credentialEnv(
	clientId: string,
	secret: string,
	syntax: EnvSyntax,
): string {
	const vars: [string, string][] = [
		["STRATONEXT_AUTH_API_CLIENT_ID", clientId],
		["STRATONEXT_AUTH_API_CLIENT_SECRET", secret],
	];
	const line = ([k, v]: [string, string]) =>
		syntax === "unix"
			? `export ${k}="${v}"`
			: syntax === "windows"
				? `$env:${k}="${v}"`
				: `${k}=${v}`;
	return vars.map(line).join("\n");
}

interface AgentEnvBlockProps {
	clientId: string;
	/** What the copy button puts on the clipboard for the secret. */
	secret: string;
	/** What is shown for the secret; defaults to `secret`. Lets a real secret stay out of view. */
	displaySecret?: string;
}

/**
 * The env vars that make the CLI or MCP server run as an agent (ADR-060), with a shell picker that
 * defaults to the viewer's own OS.
 */
export function AgentEnvBlock({
	clientId,
	secret,
	displaySecret = secret,
}: AgentEnvBlockProps) {
	const [syntax, setSyntax] = useState<EnvSyntax>(() =>
		navigator.userAgent.includes("Windows") ? "windows" : "unix",
	);
	return (
		<Space vertical style={{ width: "100%" }}>
			<Segmented
				options={ENV_SYNTAX_OPTIONS}
				value={syntax}
				onChange={(value) => setSyntax(value as EnvSyntax)}
				block
			/>
			<CodeBlock copyText={credentialEnv(clientId, secret, syntax)}>
				{credentialEnv(clientId, displaySecret, syntax)}
			</CodeBlock>
		</Space>
	);
}

/**
 * A copyable multi-line snippet with the same soft background as inline `code`. A block, not
 * inline `code`, whose padding indented only the first line.
 */
export function CodeBlock({
	children,
	copyText = children,
}: {
	children: string;
	/** What the copy button puts on the clipboard; defaults to the shown text. */
	copyText?: string;
}) {
	const {
		token: { colorFillSecondary, padding, borderRadius },
	} = theme.useToken();
	return (
		<Typography.Paragraph
			copyable={{ ...COPYABLE, text: copyText }}
			style={{
				margin: 0,
				backgroundColor: colorFillSecondary,
				padding,
				borderRadius,
				fontFamily: "monospace",
				whiteSpace: "pre-wrap",
				wordBreak: "break-all",
			}}
		>
			{children}
		</Typography.Paragraph>
	);
}
